import React, { ReactNode, useState } from 'react';
import styles from './challengePopup.module.scss';
import PlayerRow from '../../common/playerRow/playerRow';
import Button from '@ui-kit/components/button';
import { ReactComponent as ChallengeIcon } from '../../../assets/swords.svg';
import { ChallengeParams, IChallengeService } from '../../../model/interfaces';
import { Moment } from 'moment';
import { Loader } from '@ui-kit/components/loader';
import { container } from 'tsyringe';
import ChallengeDatePicker from '../challengeDatePicker/challengeDatePicker';
import { t } from '@base/utils/helpers';

export default function ChallengePopup(props: { userId?: string; avatar?: string; name: string }) {
  const challengeService: IChallengeService = container.resolve('challengeService');
  const [data, setData] = useState<Record<string, any>>({
    date: null,
    message: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleFormChange = (name: string, value: any) => {
    setData((data) => ({ ...data, [name]: value }));
  };

  const handleCreateChallenge = async () => {
    if (!data.date) return;
    try {
      setLoading(true);
      const params: ChallengeParams = {
        day: (data.date as Moment).format('YYYY MMM DD'),
        hour: (data.date as Moment).format('hh'),
        ampm: (data.date as Moment).format('A'),
        minute: (data.date as Moment).format('mm'),
        rid: props.userId,
        rusr: props.name,
        note: data.message,
      };
      await challengeService.sendChallengeRequest(params);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (error) return <ErrorCreatingChallenge name={props.name} onClick={() => setError(false)} />;
  if (success) return <SuccessCreatingChallenge name={props.name} />;

  return (
    <ChallengePopupWrapper>
      <header>
        {t('challenge')} <PlayerRow name={props.name} avatar={props.avatar} />
      </header>
      {loading ? (
        <Loader />
      ) : (
        <ChallengeForm
          onChange={handleFormChange}
          onCreate={handleCreateChallenge}
          opponentName={props.name}
          values={data}
        />
      )}
    </ChallengePopupWrapper>
  );
}

function ChallengePopupWrapper(props: { children: ReactNode }) {
  return <div className={styles.challengePopup}>{props.children}</div>;
}

function ChallengeForm(props: {
  onChange: (name: string, value: any) => void;
  values: Record<string, any>;
  onCreate: () => void;
  opponentName: string;
}) {
  return (
    <div>
      <div className={styles.picker}>
        <span>{t('date_of_challenge')}</span>
        <ChallengeDatePicker date={props.values.date} onChange={(value) => props.onChange('date', value)} />
      </div>
      <textarea
        name="message"
        rows={10}
        onChange={(e) => props.onChange('message', e.target.value)}
        placeholder={t('type_your_challenge_message_to_arg', props.opponentName)}
      ></textarea>
      <Button onClick={props.onCreate} color="default" icon={<ChallengeIcon fill="var(--lemon)" />}>
        {t('create_challenge')}
      </Button>
    </div>
  );
}

function MessageWrapper(props: { children: ReactNode }) {
  return <div className={styles.messageWrapper}>{props.children}</div>;
}

function ErrorCreatingChallenge(props: { name: string; onClick: () => void }) {
  return (
    <ChallengePopupWrapper>
      <MessageWrapper>
        <h1>{t('error_creating_challenge')}</h1>
        <p onClick={props.onClick}>{t('please_try_again')}</p>
      </MessageWrapper>
    </ChallengePopupWrapper>
  );
}

function SuccessCreatingChallenge(props: { name: string }) {
  return (
    <ChallengePopupWrapper>
      <MessageWrapper>
        <h1>{t('challenge_created')}</h1>
        <p>{t('your_challenge_created', props.name)}</p>
      </MessageWrapper>
    </ChallengePopupWrapper>
  );
}
